import { Component, Input, OnInit, signal, WritableSignal } from '@angular/core';
import { MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoModule } from '@jsverse/transloco';
import { CompanyService } from 'app/core/company/company.service';
import { Company, InvalidCompany } from 'app/core/company/company.types';
import { CurrentUserService } from 'app/core/user/current-user.service';
import { InvalidUser, User } from 'app/core/user/user.types';
import { take } from 'rxjs';

@Component({
    selector: 'app-profile-progress-bar',
    standalone: true,
    imports: [MatProgressBarModule, TranslocoModule, MatIcon, MatTooltip, MatPrefix],
    templateUrl: './profile-progress-bar.component.html',
    styleUrls: ['./profile-progress-bar.component.scss'],
})
export class ProfileProgressBarComponent implements OnInit {

    @Input() activateTooltip:boolean = true
    private _progress: WritableSignal<number> = signal(0);

    get progress(): number {
        return this._progress();
    }

    constructor(
        private userService: CurrentUserService,
        private companyService: CompanyService
    ) {}

    ngOnInit(): void {
        this.calculateProgress();
    }

    private calculateProgress(): void {
        this.userService.user$.subscribe({
            next: (user: User) => {
                const userFields = this.calculateUserProgress(user);

                const companyId = this.userService.userCompany()?.id;
                if (!companyId) {
                    const totalFields = 8;
                    const progressPercentage = Math.round(
                        (userFields / totalFields) * 100
                    );
                    this._progress.set(progressPercentage);
                    return;
                }

                this.userService.user$.pipe(take(1)).subscribe({
                    next: (user) => {
                        const company = user?.companies?.find(c => c.id === companyId);
                        const totalFields = 8;
                        const companyFields = company ? this.calculateCompanyProgress(company) : 0;
                        const completedFields = userFields + companyFields;
                        this._progress.set(Math.round((completedFields / totalFields) * 100));
                    },
                    error: (error) => {
                        console.error('Error fetching company data:', error);
                        const totalFields = 8;
                        const progressPercentage = Math.round(
                            (userFields / totalFields) * 100
                        );
                        this._progress.set(progressPercentage);
                    },
                });
            },
            error: (error) => {
                console.error('Error fetching user data:', error);
                this._progress.set(0);
            },
        });
    }

    private calculateUserProgress(user: User): number {
        let completedFields = 0;
        if (user !== InvalidUser) {
            if (user.name?.trim()) completedFields++;
            if (user.surname?.trim()) completedFields++;
            if (user.email?.trim()) completedFields++;
            if (user.telephone?.trim()) completedFields++;
        }
        return completedFields;
    }

    private calculateCompanyProgress(company: Company): number {
        let completedFields = 0;
        if (company !== InvalidCompany) {
            if (company.name?.trim()) completedFields++;
            if (company.nif?.trim()) completedFields++;
            if (company.countryId) completedFields++;
            if (company.postalCode?.trim()) completedFields++;
        }
        return completedFields;
    }
}
